
import { SpaceTimeClient, type ItemBarSlotRecord } from '../spacetime/SpaceTimeClient';
import { EventHub } from '../events/EventHub';
import { INPUT_INTENT_EVENT } from '../controller/InputController';
import { normalizeInputTypes } from '../model/Input';
import { t } from '../i18n';
import { localizeItemName } from '../i18n/content';
import { ActionTooltip } from './ActionTooltip';
import { createItemTooltipContent, mergeItemMetadata, parseItemInputs } from './itemTooltip';
import type { ItemCatalogEntry } from '../spacetime/types';

export class ItemBar {
    private container: HTMLElement;
    private client: SpaceTimeClient;
    private eventHub: EventHub;
    private items: ItemBarSlotRecord[] = [];
    private selectedIndex = 0;
    private inventoryOverlay: HTMLElement;
    private readonly tooltip = new ActionTooltip();
    private itemCatalogById = new Map<string, ItemCatalogEntry>();

    constructor(
        container: HTMLElement,
        client: SpaceTimeClient,
        eventHub: EventHub,
        inventoryOverlay: HTMLElement
    ) {
        this.container = container;
        this.client = client;
        this.eventHub = eventHub;
        this.inventoryOverlay = inventoryOverlay;
    }

    public update(items: ItemBarSlotRecord[]): void {
        this.items = items;
        this.render();
    }

    public setSelectedIndex(index: number): void {
        this.selectedIndex = (index + 9) % 9;
        this.render();
    }

    public updateItemCatalog(entries: ItemCatalogEntry[]): void {
        this.itemCatalogById = new Map(entries.map((entry) => [entry.itemId, entry]));
        this.render();
    }

    public getSelectedIndex(): number {
        return this.selectedIndex;
    }

    public cycleSelection(delta: number): void {
        this.setSelectedIndex(this.selectedIndex + delta);
    }

    public useSelectedSlot(): boolean {
        return this.useSlot(this.selectedIndex);
    }

    public useSlot(barIndex: number): boolean {
        const item = this.items.find((i) => i.barIndex === barIndex);
        if (!item) return false;

        const catalogEntry = this.itemCatalogById.get(item.itemId);
        const mergedMetadata = mergeItemMetadata(item.metadata, catalogEntry?.metadata);
        const metadataName = localizeItemName(item.itemId, mergedMetadata, catalogEntry?.displayName ?? item.itemId);
        console.log('[ItemBar] Using item:', metadataName);
        this.eventHub.emit(INPUT_INTENT_EVENT, {
            type: 'useItem',
            itemId: item.itemId,
            inputTypes: normalizeInputTypes(parseItemInputs(mergedMetadata)),
        });
        return true;
    }

    private render(): void {
        this.tooltip.hide();
        this.container.innerHTML = '';
        const itemMap = new Map(this.items.map((item) => [item.barIndex, item]));

        for (let barIndex = 0; barIndex < 9; barIndex += 1) {
            const slotEl = document.createElement('div');
            slotEl.className = 'item-bar-slot';
            if (barIndex === this.selectedIndex) {
                slotEl.classList.add('selected');
            }

            // Slot number label (Shift+1 through Shift+9)
            const numberEl = document.createElement('span');
            numberEl.className = 'slot-number';
            numberEl.textContent = `⇧${barIndex + 1}`;
            slotEl.appendChild(numberEl);

            if (barIndex === this.selectedIndex) {
                const selectionCursorEl = document.createElement('span');
                selectionCursorEl.className = 'selection-cursor';
                selectionCursorEl.textContent = '◀';
                slotEl.appendChild(selectionCursorEl);
            }

            slotEl.addEventListener('click', () => {
                this.setSelectedIndex(barIndex);
            });

            const item = itemMap.get(barIndex);
            if (item) {
                slotEl.classList.add('filled');
                // Make item draggable
                slotEl.setAttribute('draggable', 'true');
                slotEl.setAttribute('data-item-id', item.itemId);
                slotEl.setAttribute('data-inventory-id', item.inventoryId);

                const metadataEmoji =
                    typeof item.metadata['emoji'] === 'string'
                        ? (item.metadata['emoji'] as string)
                        : '📦';
                const catalogEntry = this.itemCatalogById.get(item.itemId);
                const mergedMetadata = mergeItemMetadata(item.metadata, catalogEntry?.metadata);
                const mergedEmoji = typeof mergedMetadata.emoji === 'string' ? mergedMetadata.emoji : metadataEmoji;

                const emojiEl = document.createElement('span');
                emojiEl.className = 'emoji';
                emojiEl.textContent = mergedEmoji;
                slotEl.appendChild(emojiEl);

                if (item.quantity > 1) {
                    const qtyEl = document.createElement('span');
                    qtyEl.className = 'quantity';
                    qtyEl.textContent = t('inventory.itemQuantity', { qty: item.quantity });
                    slotEl.appendChild(qtyEl);
                }

                // Left-click to use item
                slotEl.addEventListener('click', () => {
                    this.useSlot(barIndex);
                });
                slotEl.addEventListener('mouseenter', (event) => {
                    this.showItemTooltip(item, event);
                });
                slotEl.addEventListener('mousemove', (event) => {
                    this.tooltip.move(event.clientX, event.clientY);
                });
                slotEl.addEventListener('mouseleave', () => {
                    this.tooltip.hide();
                });
                slotEl.addEventListener('mousedown', () => {
                    this.tooltip.hide();
                });

                // Right-click to remove from bar (only when inventory is open)
                slotEl.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    if (!this.inventoryOverlay.hidden) {
                        this.client.inventory
                            .removeFromItemBar(item.inventoryId)
                            .catch((err) => console.error('[ItemBar] Failed to remove item:', err));
                    }
                });
            }

            this.container.appendChild(slotEl);
        }
    }

    private showItemTooltip(item: ItemBarSlotRecord, event: MouseEvent): void {
        const catalogMetadata = this.itemCatalogById.get(item.itemId)?.metadata;
        this.tooltip.show(
            createItemTooltipContent(item.itemId, item.quantity, item.metadata, { catalogMetadata, tradeable: item.tradeable }),
            event.clientX,
            event.clientY
        );
    }
}
